<template>
  <a-dropdown
    trigger="click"
    :popup-max-height="600"
    position="br"
  >
    <div
      class="study-switcher"
      :class="{ loading: isLoading }"
    >
      <icon-folder class="study-icon" />
      <div class="study-info">
        <span
          v-if="currentStudy"
          class="study-name"
        >
          {{ currentStudy.study.title }}
        </span>
        <span
          v-else
          class="study-name placeholder"
        >选择研究</span>
        <span
          v-if="currentStudy"
          class="study-code"
        >
          {{ currentStudy.study.code }}
        </span>
      </div>
      <icon-down class="arrow-icon" />
    </div>

    <template #content>
      <div class="study-switcher-panel">
        <!-- Search box -->
        <div class="panel-header">
          <a-input-search
            v-model="searchKeyword"
            placeholder="搜索研究名称或编号"
            allow-clear
            size="small"
          />
        </div>

        <!-- Filter tabs -->
        <a-tabs
          v-model:active-key="filterStatus"
          size="small"
          type="rounded"
        >
          <a-tab-pane
            key="ALL"
            title="全部"
          />
          <a-tab-pane
            key="DRAFT"
            title="草案"
          />
          <a-tab-pane
            key="INITIATED"
            title="启动"
          />
          <a-tab-pane
            key="ACTIVE"
            title="活跃"
          />
          <a-tab-pane
            key="FROZEN"
            title="冻结"
          />
        </a-tabs>

        <!-- Study list -->
        <div class="study-list">
          <!-- Loading state -->
          <div
            v-if="isLoading"
            class="loading-state"
          >
            <a-spin />
            <div class="loading-text">
              加载研究列表...
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-else-if="filteredStudies.length === 0"
            class="empty-state"
          >
            <a-empty :description="availableStudies.length === 0 ? '暂无研究数据' : '未找到匹配的研究'" />
          </div>

          <!-- Study list items -->
          <div
            v-for="study in filteredStudies"
            v-else
            :key="study.id"
            class="study-item"
            :class="{ active: study.studyId === currentStudyId }"
            @click="handleSwitchStudy(study.studyId)"
          >
            <!-- Favorite icon -->
            <icon-star-fill
              v-if="study.isFavorite"
              class="favorite-icon active"
              style="color: #f7ba1e"
            />
            <icon-star
              v-else
              class="favorite-icon"
            />

            <!-- Study info -->
            <div class="study-content">
              <div class="study-header">
                <span class="study-title">{{ study.study.title }}</span>
                <a-tag
                  :color="getStatusColor(study.study.status)"
                  size="small"
                >
                  {{ getStatusLabel(study.study.status) }}
                </a-tag>
              </div>
              <div class="study-meta">
                <span class="meta-item">{{ study.study.code }}</span>
                <span class="meta-divider">•</span>
                <span class="meta-item">{{ study.roleDisplay }}</span>
                <span
                  v-if="study.lastVisitedAt"
                  class="meta-divider"
                >•</span>
                <span
                  v-if="study.lastVisitedAt"
                  class="meta-item last-visited"
                >
                  {{ formatLastVisited(study.lastVisitedAt) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </a-dropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useStudyContextStore } from '@/stores/studyContext'
import { useAuthStore } from '@/stores/auth'
import IconFolder from '@arco-design/web-vue/es/icon/icon-folder'
import IconDown from '@arco-design/web-vue/es/icon/icon-down'
import IconStar from '@arco-design/web-vue/es/icon/icon-star'
import IconStarFill from '@arco-design/web-vue/es/icon/icon-star-fill'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/zh-cn'

dayjs.extend(relativeTime)
dayjs.locale('zh-cn')

const studyStore = useStudyContextStore()
const authStore = useAuthStore()

// State
const searchKeyword = ref('')
const filterStatus = ref('ALL')

// 使用 storeToRefs 确保响应式
const {
  currentStudy,
  currentStudyId,
  uniqueStudies,
  isLoading,
} = storeToRefs(studyStore)

// 用于显示的研究列表（按 studyId 去重）
const availableStudies = uniqueStudies

const filteredStudies = computed(() => {
  let studies = availableStudies.value

  console.log('[StudySwitcher] filteredStudies computed')
  console.log('[StudySwitcher] availableStudies.value:', availableStudies.value)
  console.log('[StudySwitcher] availableStudies count:', studies.length)
  console.log('[StudySwitcher] filterStatus:', filterStatus.value)
  console.log('[StudySwitcher] searchKeyword:', searchKeyword.value)

  // Filter by status
  if (filterStatus.value !== 'ALL') {
    studies = studies.filter(s => s.study.status === filterStatus.value)
    console.log('[StudySwitcher] After status filter:', studies.length)
  }

  // Filter by search keyword
  if (searchKeyword.value) {
    const keyword = searchKeyword.value.toLowerCase()
    studies = studies.filter(s =>
      s.study.title.toLowerCase().includes(keyword) ||
      s.study.code.toLowerCase().includes(keyword)
    )
    console.log('[StudySwitcher] After search filter:', studies.length)
  }

  // Sort: favorites first, then by last visited
  const sorted = studies.sort((a, b) => {
    if (a.isFavorite && !b.isFavorite) return -1
    if (!a.isFavorite && b.isFavorite) return 1

    const aTime = a.lastVisitedAt ? new Date(a.lastVisitedAt).getTime() : 0
    const bTime = b.lastVisitedAt ? new Date(b.lastVisitedAt).getTime() : 0
    return bTime - aTime
  })

  console.log('[StudySwitcher] Final filtered studies count:', sorted.length)
  console.log('[StudySwitcher] Final filtered studies:', sorted)
  return sorted
})

// Methods
const handleSwitchStudy = async (studyId: string) => {
  await studyStore.switchStudy(studyId)
}

const getStatusColor = (status: string) => {
  const colorMap: Record<string, string> = {
    DRAFT: 'gray',
    INITIATED: 'blue',
    ACTIVE: 'green',
    CLEANED: 'cyan',
    FROZEN: 'orange',
    LOCKED: 'red',
  }
  return colorMap[status] || 'default'
}

const getStatusLabel = (status: string) => {
  const labelMap: Record<string, string> = {
    DRAFT: '草案',
    INITIATED: '启动',
    ACTIVE: '上线',
    CLEANED: '清洁',
    FROZEN: '冻结',
    LOCKED: '锁定',
  }
  return labelMap[status] || status
}

const formatLastVisited = (dateStr: string) => {
  return dayjs(dateStr).fromNow()
}

// Lifecycle
onMounted(async () => {
  console.log('[StudySwitcher] Component mounted')
  console.log('[StudySwitcher] authStore.user:', authStore.user)
  console.log('[StudySwitcher] availableStudies count:', availableStudies.value.length)

  // Load available studies if user is already authenticated
  if (authStore.user?.id) {
    console.log('[StudySwitcher] User authenticated on mount, restoring study context')
    await studyStore.restoreStudyContext()
    console.log('[StudySwitcher] After restore, availableStudies count:', availableStudies.value.length)
    console.log('[StudySwitcher] Current studyId:', currentStudyId.value)
  } else {
    console.log('[StudySwitcher] No user authenticated on mount')
  }
})

// Watch for user authentication changes
watch(
  () => authStore.user,
  async (newUser, oldUser) => {
    const newSignature = newUser ? `${newUser.id}|${newUser.username}|${newUser.role}` : ''
    const oldSignature = oldUser ? `${oldUser.id}|${oldUser.username}|${oldUser.role}` : ''
    console.log('[StudySwitcher] User watch triggered')
    console.log('[StudySwitcher] Old user:', oldUser)
    console.log('[StudySwitcher] New user:', newUser)

    if (newSignature && newSignature !== oldSignature) {
      console.log('[StudySwitcher] User changed, restoring study context')
      await studyStore.restoreStudyContext()
      console.log('[StudySwitcher] After restore, availableStudies count:', availableStudies.value.length)
      console.log('[StudySwitcher] Current studyId:', currentStudyId.value)
    }
  }
)
</script>

<style scoped>
.study-switcher {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  max-width: 300px;
  background: transparent;
}

.study-switcher:hover {
  background: var(--color-fill-2);
}

.study-switcher.loading {
  opacity: 0.6;
  cursor: not-allowed;
}

.study-icon {
  font-size: 18px;
  color: var(--color-text-2);
  flex-shrink: 0;
}

.study-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.study-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.study-name.placeholder {
  color: var(--color-text-3);
}

.study-code {
  font-size: 12px;
  color: var(--color-text-3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.arrow-icon {
  font-size: 14px;
  color: var(--color-text-3);
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.study-switcher:hover .arrow-icon {
  transform: translateY(2px);
}

/* Panel styles */
.study-switcher-panel {
  width: 400px;
  max-height: 600px;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 12px;
  border-bottom: 1px solid var(--color-border-2);
}

.study-list {
  flex: 1;
  overflow-y: auto;
  max-height: 450px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 12px;
}

.loading-text {
  font-size: 14px;
  color: var(--color-text-3);
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
}

.study-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  border-bottom: 1px solid var(--color-border-1);
}

.study-item:hover {
  background: var(--color-fill-1);
}

.study-item.active {
  background: var(--color-primary-light-1);
  border-left: 3px solid var(--color-primary);
}

.favorite-icon {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--color-text-4);
}

.favorite-icon.active {
  color: #f7ba1e;
}

.study-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.study-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.study-title {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.study-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 12px;
  color: var(--color-text-3);
}

.meta-item {
  flex-shrink: 0;
}

.meta-divider {
  color: var(--color-text-4);
}

.last-visited {
  font-style: italic;
}
</style>
